import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';

// ============================================================================
// Scroll-linked horizontal drift
// ============================================================================

/**
 * Moves a single element horizontally in response to page scroll.
 *
 * As the section travels through the viewport, `progress` goes 0 → 1 and the
 * target element drifts from left to right, so the headline appears to "move
 * across the page" as the visitor scrolls down. The static backdrop behind it
 * does not move.
 *
 * The transform is written straight to the DOM node inside a rAF-throttled
 * scroll handler (no React re-render per frame). SSG-safe (guards `window`)
 * and disabled under prefers-reduced-motion.
 */
function useScrollDrift(
  sectionRef: React.RefObject<HTMLElement | null>,
  targetRef: React.RefObject<HTMLDivElement | null>,
  /** Total horizontal travel as a fraction of the viewport width. */
  travelFraction = 0.55,
): void {
  useEffect(() => {
    const section = sectionRef.current;
    const target = targetRef.current;
    if (!section || !target) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;

    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 when the section's top first hits the bottom of the viewport,
      // 1 once its bottom has passed the top of the viewport.
      const span = rect.height + vh;
      const progress = span > 0 ? (vh - rect.top) / span : 0.5;
      const clamped = Math.min(1, Math.max(0, progress));
      // Centered range: shifted left at the top of the range, right at the
      // bottom, so the headline sweeps rightward across the page while scrolling.
      const shift = (clamped - 0.5) * vw * travelFraction;
      target.style.transform = `translate3d(${shift}px, 0, 0)`;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sectionRef, targetRef, travelFraction]);
}

// ============================================================================
// Fit-to-width sizing
// ============================================================================

/**
 * Dynamically sizes the `target` element's font so its (single-line) text
 * spans the full width of `container`. Recomputes on container resize and on
 * text-content mutations, so inline edits stay flush to the section edges.
 *
 * Works by measuring the element's natural width at a fixed baseline font-size
 * and scaling to the container. Uses inline `style.fontSize` so it overrides
 * any Tailwind class on the same element, and font-size cascades to any child
 * text nodes (e.g. the `<span>` rendered inside <RichTextContent>).
 *
 * SSG-safe: the effect only runs on the client; the outer element also keeps a
 * `clamp()` Tailwind class as a first-paint fallback before JS runs.
 */
function useFitText(
  containerRef: React.RefObject<HTMLElement | null>,
  targetRef: React.RefObject<HTMLElement | null>,
  minPx = 24,
  maxPx = 320,
): void {
  useEffect(() => {
    const container = containerRef.current;
    const target = targetRef.current;
    if (!container || !target) return;

    const fit = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) return;
      // Measure at a fixed baseline to avoid compounding rounding errors when
      // re-fitting from a previously-set inline font-size.
      const baseline = 100;
      target.style.fontSize = `${baseline}px`;
      const naturalWidth = target.scrollWidth;
      if (naturalWidth <= 0) return;
      const ratio = containerWidth / naturalWidth;
      const next = Math.max(minPx, Math.min(maxPx, baseline * ratio));
      target.style.fontSize = `${next}px`;
    };

    fit();

    const ro = new ResizeObserver(() => fit());
    ro.observe(container);

    // Re-fit whenever the tokenized text is edited inline.
    const mo = new MutationObserver(() => fit());
    mo.observe(target, { childList: true, characterData: true, subtree: true });

    // Web fonts change glyph metrics after load; re-measure once they settle.
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(fit).catch(() => undefined);
    }

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [containerRef, targetRef, minPx, maxPx]);
}

// ============================================================================
// Component
// ============================================================================

export function LiveWhereYouWant() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const headlineRef = useRef<HTMLDivElement | null>(null);
  const backdropContainerRef = useRef<HTMLDivElement | null>(null);
  const backdropTextRef = useRef<HTMLSpanElement | null>(null);

  useScrollDrift(sectionRef, headlineRef);
  useFitText(backdropContainerRef, backdropTextRef);

  return (
    <section
      ref={sectionRef}
      aria-label="Live where you want"
      className="relative w-full overflow-hidden bg-[var(--vibe-color-background)] py-2 md:py-4"
    >
      {/* Static oversized backdrop — scales to fill section width edge-to-edge. */}
      <div
        ref={backdropContainerRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex select-none items-center justify-center"
      >
        <span
          ref={backdropTextRef}
          className="font-heading font-bold uppercase leading-none tracking-[-0.02em] whitespace-nowrap text-[clamp(2rem,10vw,10rem)] text-[var(--vibe-color-primary)]"
        >
          <RichTextContent
            path="livewhereyouwant.backdrop"
            label="Backdrop Text"
            as="span"
            className="[&_*]:inline [&_p]:m-0"
          />
        </span>
      </div>

      {/* Foreground headline — the single scrolling element. Extra top padding
          nudges it below the backdrop's vertical center so it overlaps the
          lower half of the backdrop, matching the reference. */}
      <div className="relative flex min-h-[clamp(4rem,15vw,11rem)] items-center px-4 pt-[clamp(1rem,5vw,3rem)] sm:px-8 lg:px-16">
        <div ref={headlineRef} className="will-change-transform">
          <RichTextContent
            path="livewhereyouwant.headline"
            label="Headline"
            as="span"
            className="font-heading font-bold leading-none tracking-[-0.01em] whitespace-nowrap text-[clamp(1.75rem,5vw,4.5rem)] text-[var(--vibe-color-secondary)] [&_*]:inline [&_p]:m-0 [&_a]:text-[var(--vibe-color-secondary)]"
          />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'LiveWhereYouWant',
  displayName: 'Live Where You Want',
  tokens: {
    'livewhereyouwant.backdrop': {
      label: 'Backdrop Text',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Student Living</p>',
    },
    'livewhereyouwant.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Live where you want</p>',
    },
  },
};

(LiveWhereYouWant as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LiveWhereYouWant;
